import { type ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';

type ModalPortalProps = {
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
};

export const ModalPortal = ({ children, isOpen, onClose }: ModalPortalProps) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const portalRoot = document.getElementById('portal-root');
  if (!portalRoot) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={() => {
        onClose();
      }}
    >
      <div className="absolute inset-0 bg-black/80" />
      <div
        className="relative z-10 flex items-center justify-center p-6 pointer-events-none"
        style={{ width: '100%', height: '100%' }}
      >
        <div
          className="pointer-events-auto"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {children}
        </div>
      </div>
    </div>,
    portalRoot,
  );
};
